import { getEchoInstance } from '../echo';
import storage from './storage';

// Define notification data types
export interface NotificationData {
  id: string;
  type: string;
  data: {
    message: string;
    title?: string;
    action_url?: string;
    spotlight?: boolean;
    action?: string;
    [key: string]: any;
  };
  read_at: string | null;
  created_at: string;
  spotlight?: boolean;
}

// Define handler types
export type NotificationHandler = (notification: NotificationData) => void | Promise<void>;
export type EventHandler = (data: any) => void | Promise<void>;

// Channel manager class
export class EchoChannelManager {
  private echo: any = null;
  private activeChannels: Set<string> = new Set();
  private handlers: Map<string, NotificationHandler | EventHandler> = new Map();
  private isInitialized = false;

  // Initialize Echo instance
  async initialize(): Promise<boolean> {
    if (this.isInitialized && this.echo) {
      console.log('📡 Echo channels already initialized');
      return true;
    }

    try {
      this.echo = await getEchoInstance();
      if (!this.echo) {
        console.error('❌ Failed to get Echo instance');
        return false;
      }

      console.log('✅ Echo channel manager initialized');
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('❌ Error initializing Echo channel manager:', error);
      return false;
    }
  }

  // Subscribe to notification channels for a user
  async subscribeToNotificationChannels(
    user: { workos_id?: string }, 
    tenantUuid: string,
    notificationHandler: NotificationHandler
  ): Promise<string[]> {
    if (!this.isInitialized || !this.echo) {
      console.error('❌ Echo not initialized');
      return [];
    }

    const channels: string[] = [];

    try {
      // Subscribe to tenant channel
      if (user.workos_id && tenantUuid) {
        const tenantChannel = `Tenant.${tenantUuid}.User.${user.workos_id}`;
        
        // Skip if already subscribed
        if (!this.activeChannels.has(tenantChannel)) {
          channels.push(tenantChannel);
          
          console.log(`📡 Subscribing to tenant channel: ${tenantChannel}`);
          const channel = this.echo.private(tenantChannel);
          
          // Listen for notifications
          channel.listen('.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated', notificationHandler);
          
          // Also listen for spotlight published events on this same channel
          channel.listen('spotlight.published', (event: any) => {
            console.log('🔦 Spotlight published event received on private user channel:', event);
            
            // Dispatch custom event with spotlight ID for SpotlightManager to pick up
            const spotlightRefreshEvent = new CustomEvent('spotlight_refresh', {
              detail: {
                spotlight_id: event.spotlight?.id,
                spotlight: event.spotlight,
                published_by: event.published_by,
                timestamp: event.timestamp,
                tenant_id: event.tenant_id
              }
            });
            
            console.log('🔦 Dispatching spotlight refresh event with preferred ID:', event.spotlight?.id);
            window.dispatchEvent(spotlightRefreshEvent);
          });
          
          // Also listen for dotted version
          channel.listen('.spotlight.published', (event: any) => {
            console.log('🔦 Dotted spotlight published event received on private user channel:', event);
            
            // Dispatch custom event with spotlight ID for SpotlightManager to pick up  
            const spotlightRefreshEvent = new CustomEvent('spotlight_refresh', {
              detail: {
                spotlight_id: event.spotlight?.id,
                spotlight: event.spotlight,
                published_by: event.published_by,
                timestamp: event.timestamp,
                tenant_id: event.tenant_id
              }
            });
            
            console.log('🔦 Dispatching dotted spotlight refresh event with preferred ID:', event.spotlight?.id);
            window.dispatchEvent(spotlightRefreshEvent);
          });
          
          this.activeChannels.add(tenantChannel);
        }
      }

      console.log(`✅ Successfully subscribed to ${channels.length} notification channels`);
      return channels;
    } catch (error) {
      console.error('❌ Error subscribing to notification channels:', error);
      return [];
    }
  }

  // Subscribe to settings refresh events
  async subscribeToSettingsEvents(
    user: { workos_id?: string }, 
    tenantUuid: string,
    settingsHandler: EventHandler
  ): Promise<string[]> {
    if (!this.isInitialized || !this.echo) {
      console.error('❌ Echo not initialized');
      return [];
    }

    const channels: string[] = [];

    try {
      // NEW: Subscribe to public tenant channel for global settings refresh
      if (tenantUuid) {
        const globalTenantChannel = `Tenant.${tenantUuid}`;
        
        // Skip if already subscribed
        if (this.activeChannels.has(globalTenantChannel)) {
          console.log(`⚠️ Already subscribed to ${globalTenantChannel}, skipping...`);
          return Array.from(this.activeChannels);
        }
        
        channels.push(globalTenantChannel);
        
        console.log(`⚙️ Subscribing to GLOBAL tenant settings channel: ${globalTenantChannel}`);
        
        const channel = this.echo.private(globalTenantChannel);
        
        // Subscribe first, then set up listeners
        channel.subscribed(() => {
          console.log(`✅ Successfully subscribed to GLOBAL ${globalTenantChannel}`);
          
          // Now set up the listeners after successful subscription
          channel.listen('.SettingsRefreshed', (event: any) => {
            
            if (event.action === 'refresh_settings') {
              
              // Call the settings handler
              settingsHandler(event);
              
              // Also dispatch the legacy custom event for backwards compatibility
              const customEvent = new CustomEvent('onespace-settings-refresh', {
                detail: {
                  title: event.title,
                  message: event.message,
                  timestamp: event.time,
                  tenantId: event.tenant_id
                }
              });
            
              window.dispatchEvent(customEvent);
            } else {
              console.log('❌ Event action is not refresh_settings:', event.action);
            }
          });
        });
        
        // Debug: Listen for all events on this channel
        if (channel.bind_global) {
          channel.bind_global((eventName: string, data: any) => {
            console.log(`🔍 DEBUG: Event received on ${globalTenantChannel}:`, eventName, data);
          });
        }
        
        // Error handling
        channel.error((error: any) => {
          console.error(`❌ Error on GLOBAL channel ${globalTenantChannel}:`, error);
        });
        
        this.activeChannels.add(globalTenantChannel);
      }

      console.log(`✅ Successfully subscribed to settings events on ${channels.length} channels`);
      return channels;
    } catch (error) {
      console.error('❌ Error subscribing to settings events:', error);
      return [];
    }
  }

  // Subscribe to spotlight published events
  async subscribeToSpotlightEvents(
    user: { workos_id?: string }, 
    tenantUuid: string,
    spotlightHandler: EventHandler
  ): Promise<string[]> {
    if (!this.isInitialized || !this.echo) {
      console.error('❌ Echo not initialized');
      return [];
    }

    const channels: string[] = [];

    try {
      // Subscribe to tenant channel for spotlight published events
      if (tenantUuid) {
        const tenantChannel = `Tenant.${tenantUuid}`;
        
        // Skip if already subscribed (but still return current channels)
        if (this.activeChannels.has(tenantChannel)) {
          console.log(`⚠️ Already subscribed to ${tenantChannel} for spotlight events`);
          // Don't return early, we can still add the spotlight listener to existing channel
        } else {
          channels.push(tenantChannel);
        }
        
        console.log(`🔦 Setting up spotlight events on tenant channel: ${tenantChannel}`);
        
        const channel = this.echo.private(tenantChannel);
        
        // Listen for SpotlightPublished events
        channel.listen('spotlight.published', (event: any) => {
          console.log('🔦 Spotlight published event received:', event);
          console.log('🔦 Event details:', JSON.stringify(event, null, 2));
          
          // Call the spotlight handler
          spotlightHandler(event);
        });
        
        // Also listen for dotted version
        channel.listen('.spotlight.published', (event: any) => {
          console.log('🔦 Dotted spotlight published event received:', event);
          spotlightHandler(event);
        });
        
        if (!this.activeChannels.has(tenantChannel)) {
          this.activeChannels.add(tenantChannel);
        }
      }

      console.log(`✅ Successfully subscribed to spotlight events on ${channels.length} channels`);
      return channels;
    } catch (error) {
      console.error('❌ Error subscribing to spotlight events:', error);
      return [];
    }
  }

  // Subscribe to a custom channel
  async subscribeToChannel(
    channelName: string, 
    eventName: string, 
    handler: EventHandler,
    isPrivate: boolean = true
  ): Promise<boolean> {
    if (!this.isInitialized || !this.echo) {
      console.error('❌ Echo not initialized');
      return false;
    }

    try {
      console.log(`📡 Subscribing to custom channel: ${channelName} for event: ${eventName}`);
      
      const channel = isPrivate ? this.echo.private(channelName) : this.echo.channel(channelName);
      channel.listen(eventName, handler);
      
      this.activeChannels.add(channelName);
      this.handlers.set(`${channelName}:${eventName}`, handler);
      
      console.log(`✅ Successfully subscribed to ${channelName}`);
      return true;
    } catch (error) {
      console.error(`❌ Error subscribing to channel ${channelName}:`, error);
      return false;
    }
  }

  // Set up all channels for a user
  async setupUserChannels(
    user: { workos_id?: string },
    notificationHandler: NotificationHandler,
    settingsHandler: EventHandler
  ): Promise<string[]> {
    // Check if channels are already set up for this user
    if (this.activeChannels.size > 0) {
      console.log('📡 Channels already active, skipping setup:', Array.from(this.activeChannels));
      return Array.from(this.activeChannels);
    }

    if (!this.isInitialized) {
      const initialized = await this.initialize();
      if (!initialized) {
        return [];
      }
    }

    try {
      // Get tenant UUID
      const tenantUuid = await storage.getItem('onespace_tenant_uuid');
      if (!tenantUuid) {
        console.error('❌ No tenant UUID found');
        return [];
      }

      console.log(`🔧 Setting up channels for user ${user.workos_id} in tenant ${tenantUuid}`);

      // Subscribe to all channel types
      const [notificationChannels, settingsChannels] = await Promise.all([
        this.subscribeToNotificationChannels(user, tenantUuid, notificationHandler),
        this.subscribeToSettingsEvents(user, tenantUuid, settingsHandler)
      ]);

      const allChannels = [...new Set([...notificationChannels, ...settingsChannels])];
      console.log(`✅ All channels set up successfully: ${allChannels.join(', ')}`);
      
      return allChannels;
    } catch (error) {
      console.error('❌ Error setting up user channels:', error);
      return [];
    }
  }

  // Leave a specific channel
  leaveChannel(channelName: string): void {
    if (!this.echo) return;

    try {
      this.echo.leave(channelName);
      this.activeChannels.delete(channelName);
      
      // Remove handlers for this channel
      for (const [key, handler] of this.handlers.entries()) {
        if (key.startsWith(channelName + ':')) {
          this.handlers.delete(key);
        }
      }
      
      console.log(`📤 Left channel: ${channelName}`);
    } catch (error) {
      console.error(`❌ Error leaving channel ${channelName}:`, error);
    }
  }

  // Leave all channels
  leaveAllChannels(): void {
    console.log('📤 Leaving all channels...');
    
    for (const channelName of this.activeChannels) {
      this.leaveChannel(channelName);
    }
    
    this.activeChannels.clear();
    this.handlers.clear();
    console.log('✅ All channels left');
  }

  // Get current active channels
  getActiveChannels(): string[] {
    return Array.from(this.activeChannels);
  }

  // Check if manager is initialized
  isReady(): boolean {
    return this.isInitialized && !!this.echo;
  }

  // Cleanup
  destroy(): void {
    this.leaveAllChannels();
    this.echo = null;
    this.isInitialized = false;
    console.log('🗑️ Echo channel manager destroyed');
  }
}

// Create and export singleton instance
export const echoChannelManager = new EchoChannelManager();

// Export convenience functions
export const initializeEchoChannels = () => echoChannelManager.initialize();
export const setupUserChannels = (
  user: { workos_id?: string },
  notificationHandler: NotificationHandler,
  settingsHandler: EventHandler
) => echoChannelManager.setupUserChannels(user, notificationHandler, settingsHandler);
export const leaveAllChannels = () => echoChannelManager.leaveAllChannels();
export const getActiveChannels = () => echoChannelManager.getActiveChannels(); 